import { useState } from 'react';
import type { AccountSkill, AccountSkillMeta } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';
import { Button } from '@localagent/ui/components/button';
import { TrashIcon } from '@localagent/ui/components/data-list';
import { ListAddButton } from '@localagent/ui/components/list-filter-menu';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import {
  useAccountSkills,
  useCreateAccountSkill,
  useDeleteAccountSkill,
  useUpdateAccountSkill,
} from '../../hooks/useAccountSkills';

export function SkillsTab() {
  const listQuery = useAccountSkills();
  const createMutation = useCreateAccountSkill();
  const updateMutation = useUpdateAccountSkill();
  const deleteMutation = useDeleteAccountSkill();
  const skills = listQuery.data?.skills;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AccountSkillMeta | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<AccountSkill | null>(null);

  const selected = skills?.find((s) => s.id === selectedId) ?? null;
  const busy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const openCreate = () => {
    setCreating(true);
    setSelectedId(null);
    setLoaded(null);
    setName('');
    setDescription('');
    setBody('');
    setLoadError(null);
    setEditorOpen(true);
  };

  const openEdit = async (row: AccountSkillMeta) => {
    setCreating(false);
    setSelectedId(row.id);
    setLoadError(null);
    setEditorOpen(true);
    try {
      const skill = await getSDK().skills.get(row.id);
      setLoaded(skill);
      setName(skill.name);
      setDescription(skill.description);
      setBody(skill.body);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSave = async () => {
    try {
      if (creating) {
        const row = await createMutation.mutateAsync({ name, description, body });
        setSelectedId(row.id);
        setEditorOpen(false);
        return;
      }
      if (!selectedId) return;
      await updateMutation.mutateAsync({
        skillId: selectedId,
        req: { description, body },
      });
      setEditorOpen(false);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="flex flex-col gap-4 py-2" data-testid="skills-tab">
      <div className="flex items-center justify-end">
        <ListAddButton label="New skill" testId="new-skill-button" onClick={openCreate} />
      </div>

      {(listQuery.isError || loadError) && (
        <p className="text-body text-destructive" data-testid="skills-list-error">
          {loadError ??
            (listQuery.error instanceof Error ? listQuery.error.message : String(listQuery.error))}
        </p>
      )}

      <ul className="rounded-md" data-testid="skills-list">
        {skills?.map((row) => (
          <li key={row.id} className="group flex items-start justify-between gap-3 rounded-md px-3 py-2 hover:bg-accent">
            <button
              type="button"
              data-testid={`skill-row-${row.name}`}
              className="min-w-0 flex-1 text-left"
              onClick={() => void openEdit(row)}
            >
              <span className="font-medium">{row.name}</span>
              <span className="ml-2 text-caption text-muted-foreground">shogun::{row.name}</span>
              <p className="text-body text-muted-foreground">{row.description}</p>
            </button>
            <Tooltip label="Delete">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={`skill-delete-${row.name}`}
                className="h-7 w-7 p-0 text-destructive opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                onClick={() => setDeleting(row)}
              >
                <TrashIcon />
              </Button>
            </Tooltip>
          </li>
        ))}
        {skills !== undefined && skills.length === 0 && (
          <li className="px-3 py-6 text-body text-muted-foreground">No catalog skills yet.</li>
        )}
      </ul>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{creating ? 'New catalog skill' : `Edit ${loaded?.name ?? selected?.name ?? ''}`}</DialogTitle>
            <DialogDescription>
              The assistant reads this text with skill_read using shogun::name.
            </DialogDescription>
          </DialogHeader>
          {loadError && <p className="text-body text-destructive">{loadError}</p>}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill-name">Name</Label>
              <Input
                id="skill-name"
                data-testid="skill-name"
                value={name}
                disabled={!creating}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill-description">Description</Label>
              <Input
                id="skill-description"
                data-testid="skill-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill-body">Body</Label>
              <textarea
                id="skill-body"
                data-testid="skill-body"
                className="min-h-[16rem] w-full rounded-md bg-muted px-3 py-2 text-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="skill-save" disabled={busy} onClick={() => void handleSave()}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this skill?</DialogTitle>
            <DialogDescription>
              {deleting ? `"${deleting.name}" will leave the catalog. This cannot be undone.` : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="skill-delete-confirm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                const target = deleting;
                if (!target) return;
                void deleteMutation.mutateAsync(target.id).then(
                  () => setDeleting(null),
                  (err: unknown) => {
                    setLoadError(err instanceof Error ? err.message : String(err));
                  },
                );
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
